import React, { ReactElement, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { connect, useSelector } from 'react-redux';

import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { relativeDateString } from '@atlassian/bitkit-date';

import { BuildStatusConnected } from 'src/components/build-status';
import { useAnalytics } from 'src/components/settings/analytics';
import { Commit, PullRequestCommit, User } from 'src/components/types';
import { UnmatchedCommitAuthorName } from 'src/components/user-profile';
import { UserName } from 'src/containers/user-name';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { MetricSender, PR_MERGE_INTERACTION } from 'src/metrics';
import { useFixMissingCommits } from 'src/redux/pull-request/hooks/fix-missing-commits';
import {
  cancelPendingMerge,
  getIsMergeErrored,
} from 'src/redux/pull-request/merge-reducer';
import {
  getConflictStatus,
  getCurrentPullRequest,
  getCurrentPullRequestUrlPieces,
  getPullRequestCanCurrentUserMerge,
  getPullRequestDraftStatus,
  getPullRequestDraftStatusLoading,
  getPullRequestHasMissingCommitsError,
  getPullRequestIsMissingCommits,
  getPullRequestIsUpdatingCommits,
} from 'src/redux/pull-request/selectors';
import { getCurrentRepoCommits } from 'src/redux/repo-commits/selectors';
import { RepositoryPrivilege } from 'src/sections/repository/types';
import { getRepositoryAccessLevel } from 'src/selectors/repository-selectors';
import { BuildStatusesMap } from 'src/types';
import { BucketState } from 'src/types/state';
import urls from 'src/urls';
import firstLine from 'src/utils/first-line';
import { statsdApiClient } from 'src/utils/metrics';
import { shortHash } from 'src/utils/short-hash';

import { useDraftActions } from '../../hooks/draft-actions';
import { useMergeData } from '../../hooks/merge-data';
import LinkedMessage from '../linked-message';
import mergeMessages from '../merge-dialog/merge-dialog.i18n';

import { AllChecksPassedMessage } from './all-checks-passed-message';
import { MergeErroredMessage } from './merge-errored-message';
import messages from './pull-request-state-message.i18n';
import * as styles from './pull-request-state-message.style';

const mapStateToProps = (state: BucketState) => {
  const currentPullRequest =
    getCurrentPullRequest(state) || ({} as Partial<BB.PullRequest>);

  const commits = getCurrentRepoCommits(state) || [];
  const hash =
    currentPullRequest.merge_commit && currentPullRequest.merge_commit.hash;
  let mergeCommitWithExtra: Commit | undefined = undefined;
  if (commits && commits.length !== 0 && commits[0].hash === hash) {
    mergeCommitWithExtra = commits[0];
  }

  const isDraftStatusLoading = getPullRequestDraftStatusLoading(state);
  const isDraft = getPullRequestDraftStatus(state);

  return {
    pullRequestState: currentPullRequest.state,
    isDraft,
    isDraftStatusLoading,
    mergeCommit: currentPullRequest.merge_commit,
    mergeCommitWithExtra,
    closedBy: currentPullRequest.closed_by,
    closedOn: currentPullRequest.closed_on,
    reason: currentPullRequest.reason,
    hasMergeConflicts: getConflictStatus(state),
    canCurrentUserMerge: getPullRequestCanCurrentUserMerge(state),
    isMergeErrored: getIsMergeErrored(state),
    isMissingCommits: getPullRequestIsMissingCommits(state),
    isUpdatingCommits: getPullRequestIsUpdatingCommits(state),
    hasMissingCommitsError: getPullRequestHasMissingCommitsError(state),
    userRepoAccessLevel: getRepositoryAccessLevel(state),
  };
};

const mapDispatchToProps = {
  onCancelPendingMerge: cancelPendingMerge,
};

type PullRequestStateMessageProps = {
  pullRequestState: string | undefined;
  isDraft: boolean;
  isDraftStatusLoading: boolean;
  mergeCommit: PullRequestCommit | undefined;
  mergeCommitWithExtra: Commit | undefined;
  closedBy: User | undefined;
  closedOn: string | undefined;
  reason: string | undefined;
  hasMergeConflicts: boolean;
  canCurrentUserMerge: boolean;
  onCancelPendingMerge: () => void;
  isMergeErrored: boolean;
  isMissingCommits: boolean;
  isUpdatingCommits: boolean;
  hasMissingCommitsError: boolean;
  userRepoAccessLevel: RepositoryPrivilege | undefined;
};

const BuildsLink = ({
  buildCounts,
  statusUrl,
}: {
  buildCounts: BuildStatusesMap;
  statusUrl: string;
}) => {
  return (
    <BuildStatusConnected
      buildCounts={buildCounts}
      statusUrl={statusUrl}
      showIcon={false}
      buildFailureTitle={messages.loadMergeCommitBuildsFailureTitle}
      buildFailureDescription={messages.loadMergeCommitBuildsFailureDescription}
    />
  );
};

const PullRequestStateMessageBase = (props: PullRequestStateMessageProps) => {
  const {
    pullRequestState,
    isDraft,
    isDraftStatusLoading,
    mergeCommit,
    mergeCommitWithExtra,
    closedBy,
    hasMergeConflicts,
    canCurrentUserMerge,
    onCancelPendingMerge,
    isMergeErrored,
    isMissingCommits,
    isUpdatingCommits,
    hasMissingCommitsError,
    userRepoAccessLevel,
  } = props;

  const enhancedGasV3PREvents = useStatsigGate(
    StatsigFeatureKeys.enhancedGasV3PREvents
  );

  const intl = useIntl();
  const {
    isAsyncMergeInProgress,
    isMergePending,
    canShowAllChecksPassedNotification,
    requiredCustomOnMergeChecksFailedCount,
  } = useMergeData();

  const fixMissingCommits = useFixMissingCommits();
  const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);

  const { markReadyForReview } = useDraftActions();
  const areDraftPRsEnabled = useStatsigGate(StatsigFeatureKeys.bbcDraftPRs);

  const { publishClickEvent, publishTrackEvent } = useAnalytics({
    source: 'pullRequestStateMessage',
  });

  if (enhancedGasV3PREvents) {
    if (hasMissingCommitsError) {
      publishTrackEvent({
        action: 'missingCommitsError',
        attributes: {
          pr_id: id,
        },
      });
      statsdApiClient.increment(
        'frontbucket.capability.updatePullRequest.failure'
      );
    } else if (isMissingCommits && !isUpdatingCommits) {
      publishTrackEvent({
        action: 'commitsNeedsManualUpdating',
        attributes: {
          pr_id: id,
        },
      });
      statsdApiClient.increment(
        'frontbucket.capability.updatePullRequest.manual'
      );
    }
  }

  const handleMarkAsReady = () => {
    markReadyForReview();
    const attributes: { pr_id?: number } = {};

    if (enhancedGasV3PREvents) {
      attributes.pr_id = id;
    }

    publishClickEvent({
      name: 'markAsReadyStateMessageButton',
      actionSubject: 'button',
      attributes,
    });
  };

  const handleMissingCommitsClick = () => {
    fixMissingCommits({ owner, slug, id });

    if (enhancedGasV3PREvents) {
      publishClickEvent({
        name: 'missingCommitsButton',
        actionSubject: 'button',
        attributes: {
          pr_id: id,
        },
      });
      statsdApiClient.increment(
        'frontbucket.capability.updatePullRequest.clicked'
      );
    }
  };

  const closedByTextElement = (
    <FormattedMessage
      {...messages.commitClosedByKey}
      values={{
        closedBy: (
          <span>
            <UserName user={closedBy} />
          </span>
        ),
      }}
    />
  );

  // If the metric started before mount, we'll need to cancel it
  // This could happen if it started on another PR and the user quickly transitions to a different PR
  const [isMergeMetricReady, setIsMergeMetricReady] = useState<boolean>(
    PR_MERGE_INTERACTION.getData().state !== 'started'
  );

  // If a PR_MERGE_INTERACTION has already been started on mount, cancel it
  useEffect(() => {
    if (!isMergeMetricReady) {
      PR_MERGE_INTERACTION.cancel();
      setIsMergeMetricReady(true);
    }
  }, [isMergeMetricReady]);

  const mergeStatusStateMessageDisabled = useStatsigGate(
    StatsigFeatureKeys.mergeStatusStateMessageDisabled
  );

  if (pullRequestState === 'MERGED') {
    if (
      !mergeCommit ||
      !mergeCommit.message ||
      !mergeCommit.author ||
      !mergeCommit.date
    ) {
      return null;
    }

    if (mergeStatusStateMessageDisabled) {
      // During AFM migration, merges are happening in stash and the merge commits are pushed to BBC
      // this leads to the message being incorrect frequently. Temporarily just disable it for them.
      return null;
    }

    const { author } = mergeCommit;

    const mergeCommitAuthorText = (
      <FormattedMessage
        {...messages.commitAuthorKey}
        values={{
          author: author.user ? (
            <span>
              <UserName user={author.user} />
            </span>
          ) : (
            <span>
              <UnmatchedCommitAuthorName rawCommitAuthor={author.raw} />
            </span>
          ),
        }}
      />
    );

    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="success"
          title={intl.formatMessage(messages.mergedPullRequestStateMessage)}
          actions={[
            <SectionMessageAction key="hash" href={mergeCommit.links.html.href}>
              {shortHash(mergeCommit.hash)}
            </SectionMessageAction>,
            ...(mergeCommitWithExtra &&
            mergeCommitWithExtra.extra &&
            mergeCommitWithExtra.extra.status_counts
              ? [
                  <SectionMessageAction key="build">
                    {/* Note that extra.builds only contains at most one build, so we can't use it here. */}
                    {/* The status counts contains the summary of all builds though. */}
                    <BuildsLink
                      buildCounts={mergeCommitWithExtra.extra.status_counts}
                      statusUrl={mergeCommitWithExtra.links.statuses.href}
                    />
                  </SectionMessageAction>,
                ]
              : []),
            <SectionMessageAction key="author">
              {mergeCommitAuthorText}
            </SectionMessageAction>,
            ...(closedBy
              ? [
                  <SectionMessageAction key="closed-by">
                    {closedByTextElement}
                  </SectionMessageAction>,
                ]
              : []),
            <SectionMessageAction key="date">
              {mergeCommit.date
                ? relativeDateString(mergeCommit.date, intl)
                : ''}
            </SectionMessageAction>,
          ]}
        >
          <p>{firstLine(mergeCommit.message)}</p>
          {isMergeMetricReady && <MetricSender metric={PR_MERGE_INTERACTION} />}
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (pullRequestState === 'DECLINED') {
    const { closedOn } = props;
    if (!closedOn) {
      return null;
    }

    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="error"
          title={intl.formatMessage(messages.declinedPullRequestStateMessage)}
          actions={[
            ...(closedBy
              ? [
                  <SectionMessageAction key="closed-by">
                    {closedByTextElement}
                  </SectionMessageAction>,
                ]
              : []),
            <SectionMessageAction key="date">
              {relativeDateString(closedOn ?? null, intl)}
            </SectionMessageAction>,
          ]}
        >
          <p>
            <LinkedMessage message={props.reason} />
          </p>
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (isMergeErrored || requiredCustomOnMergeChecksFailedCount > 0) {
    return (
      <div data-testid="mergeErroredMessage">
        <MergeErroredMessage />
      </div>
    );
  } else if (isAsyncMergeInProgress) {
    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="information"
          title={intl.formatMessage(messages.asyncMergePullRequestStateMessage)}
        >
          {intl.formatMessage(messages.asyncPullRequestDescription)}
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (
    isMissingCommits &&
    !isUpdatingCommits &&
    !hasMissingCommitsError
  ) {
    let actions: ReactElement[] | undefined = undefined;
    let message = messages.missingCommitsPullRequestReadPermissionDescription;
    if (userRepoAccessLevel === 'write' || userRepoAccessLevel === 'admin') {
      actions = [
        <SectionMessageAction
          key="missing-commits"
          onClick={handleMissingCommitsClick}
        >
          {intl.formatMessage(messages.missingCommitsPullRequestLink)}
        </SectionMessageAction>,
      ];
      message = messages.missingCommitsPullRequestWritePermissionDescription;
    }

    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="warning"
          title={intl.formatMessage(messages.missingCommitsPullRequestMessage)}
          actions={actions}
        >
          {intl.formatMessage(message)}
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (isUpdatingCommits && !hasMissingCommitsError) {
    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="information"
          title={intl.formatMessage(messages.updatingCommitsPullRequestMessage)}
        >
          {intl.formatMessage(messages.updatingCommitsPullRequestDescription)}
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (hasMissingCommitsError) {
    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="error"
          title={intl.formatMessage(
            messages.updatingCommitsErrorPullRequestMessage
          )}
        >
          {intl.formatMessage(
            messages.updatingCommitsErrorPullRequestDescription
          )}
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (areDraftPRsEnabled && isDraft && !isDraftStatusLoading) {
    const actions = [
      <SectionMessageAction
        key="mark-as-ready-for-review"
        onClick={handleMarkAsReady}
      >
        <FormattedMessage {...messages.markAsReadyAction} />
      </SectionMessageAction>,
    ];
    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="information"
          title={intl.formatMessage(messages.draftSectionTitle)}
          actions={actions}
        >
          <FormattedMessage {...messages.draftSectionBody} />
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (hasMergeConflicts) {
    const actions = [
      <SectionMessageAction key="merge-conflict-learn-more">
        <a
          href={urls.external.resolveMergeConflictDocs}
          rel="noopener"
          target="_blank"
        >
          {intl.formatMessage(messages.mergeConflictLearnMoreLink)}
        </a>
      </SectionMessageAction>,
    ];
    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="warning"
          title={intl.formatMessage(messages.mergeConflictMessage)}
          actions={actions}
        >
          {intl.formatMessage(messages.mergeConflictDescription)}
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (isMergePending) {
    const actions = canCurrentUserMerge
      ? [
          <SectionMessageAction
            key="pending-merge-dialog"
            onClick={onCancelPendingMerge}
          >
            {intl.formatMessage(mergeMessages.cancelPendingMergeAction)}
          </SectionMessageAction>,
        ]
      : undefined;
    return (
      <styles.StateMessageWrapper>
        <SectionMessage
          appearance="information"
          title={intl.formatMessage(mergeMessages.cancelPendingMergeHeading)}
          actions={actions}
        >
          <p>
            {intl.formatMessage(mergeMessages.cancelPendingMergeDescription)}
          </p>
        </SectionMessage>
      </styles.StateMessageWrapper>
    );
  } else if (canShowAllChecksPassedNotification) {
    return (
      <div data-testid="allChecksPassedMessage">
        <AllChecksPassedMessage />
      </div>
    );
  }
  return null;
};

export const PullRequestStateMessage = connect(
  mapStateToProps,
  mapDispatchToProps
)(PullRequestStateMessageBase);
